/**
 * Aggregate Stripe Upcoming-Invoice line items so each subscription product
 * renders as a single row, even when Stripe splits the charge across
 * proration adjustments or graduated price tiers.
 *
 * Background (#1213):
 * After a screen-count update, Stripe's upcoming invoice frequently surfaces
 * two rows for the same addon — one for the "old period" credit/cleanup
 * (e.g. `qty=3, amount=$0`) and one for the "new period" charge
 * (e.g. `qty=0, amount=$30`). Rendering both verbatim shows users a
 * confusing "duplicate" addon. Aggregating by `subscription_item` collapses
 * those into a single semantically-correct row whose summed amount equals
 * the actual net charge for the addon during this billing event.
 *
 * Grouping key fallback chain (defensive — Stripe response shape varies):
 *   1. `subscription_item` (subscription line items, including proration)
 *   2. `price.product`     (catches lines without `subscription_item` set)
 *   3. `description`       (last-resort key; one-off invoice items, etc.)
 *
 * Per-group display fields are picked from the line with the largest
 * `|amount|` (the "main" charge row), so the description/quantity always
 * reflect the meaningful billing event rather than a $0 cleanup row.
 *
 * Dropped: groups whose final `amount === 0` AND `quantity === 0` — these
 * are tier-boundary placeholders that carry no information for the user.
 */

export type InvoiceLine = {
  description?: string | null;
  quantity?: number | null;
  amount?: number | null;
  subscription_item?: string | null;
  price?: { product?: string | null } | null;
};

function getGroupKey(line: InvoiceLine, fallbackIndex: number): string {
  return (
    line.subscription_item ?? line.price?.product ?? line.description ?? `__line_${fallbackIndex}`
  );
}

export function aggregateInvoiceLines(lines: readonly InvoiceLine[]): InvoiceLine[] {
  if (lines.length === 0) return [];

  // Preserve first-seen order across keys so the visible row order matches
  // Stripe's natural ordering (plan first, then addons, then prorations).
  const order: string[] = [];
  const groups = new Map<string, InvoiceLine[]>();

  lines.forEach((line, idx) => {
    const key = getGroupKey(line, idx);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)?.push(line);
  });

  return order
    .map((key) => collapseGroup(groups.get(key) ?? []))
    .filter((line): line is InvoiceLine => line !== null);
}

function collapseGroup(group: InvoiceLine[]): InvoiceLine | null {
  if (group.length === 0) return null;
  if (group.length === 1) {
    // Single line — pass through unchanged. Don't drop $0/qty=0 here:
    // a standalone $0 row may still be informative (e.g. a fully-discounted
    // trial line). Aggregation-driven dropping only applies to multi-line
    // groups where the cleanup row is redundant noise.
    return group[0];
  }

  const totalAmount = group.reduce((sum, line) => sum + (line.amount ?? 0), 0);
  // Pick the "main" line — largest absolute amount carries the most
  // meaningful description ("Tier 1 at $30/mo") versus the cleanup
  // counterpart ("Tier 1 at $0/mo"). Stable tiebreak: prefer the line with
  // a non-zero quantity, then earliest in the input order.
  const main = group.reduce((best, candidate) => {
    const bestAbs = Math.abs(best.amount ?? 0);
    const candAbs = Math.abs(candidate.amount ?? 0);
    if (candAbs > bestAbs) return candidate;
    if (candAbs < bestAbs) return best;
    if ((candidate.quantity ?? 0) !== 0 && (best.quantity ?? 0) === 0) return candidate;
    return best;
  });

  // scio1 parity + #1213 doc-comment intent: use max(quantity) across the
  // group, not `main.quantity`. Stripe's largest-amount row often has
  // quantity=0 (tier-boundary marker), while the cleanup row carries the
  // real license count (qty=3). Taking max gives the semantically-correct
  // unit count the user is being billed for.
  const totalQuantity = group.reduce((max, line) => Math.max(max, line.quantity ?? 0), 0);

  if (totalAmount === 0 && totalQuantity === 0) return null;

  return {
    description: main.description,
    quantity: totalQuantity,
    amount: totalAmount,
    subscription_item: main.subscription_item,
    price: main.price,
  };
}
